import { StyleSheet, Text, View } from 'react-native';
import { BellRing } from 'lucide-react-native';

import { Button } from '@/src/components/Button';
import { useToast } from '@/src/components/Toast';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import { PwaInstallCard } from './PwaInstallCard';
import { useWebPush } from './use-web-push';

export function WebPushSettingsPanel() {
  const push = useWebPush();
  const { showError, showSuccess } = useToast();

  async function handleEnable() {
    try {
      await push.enable();
      showSuccess('Push notifications are enabled on this device.');
    } catch (error) {
      showError(error, 'Unable to enable push notifications.');
    }
  }

  async function handleDisable() {
    try {
      await push.disable();
      showSuccess('Push notifications are disabled on this device.');
    } catch (error) {
      showError(error, 'Unable to disable push notifications.');
    }
  }

  const status = !push.supported
    ? 'This browser does not support Frienzzi web push.'
    : push.enabledOnThisDevice
      ? 'Push notifications are enabled on this device.'
      : push.permission === 'denied'
        ? 'Browser notification permission is blocked. Enable it in browser settings before trying again.'
        : push.isStandalone
          ? 'Enable browser push notifications for this installed Frienzzi app.'
          : 'Install Frienzzi first, then open the installed app to enable push notifications.';

  return (
    <View style={styles.panelBody}>
      <PwaInstallCard dismissible={false} ignoreDismissed compact />
      <View style={styles.statusRow}>
        <View style={styles.iconShell}>
          <BellRing color={colors.primary} size={22} strokeWidth={2.5} />
        </View>
        <View style={styles.statusCopy}>
          <Text style={styles.statusTitle}>Push on this device</Text>
          <Text style={styles.statusText}>{status}</Text>
        </View>
      </View>
      {push.enabledOnThisDevice ? (
        <Button
          title="Disable push on this device"
          variant="secondary"
          loading={push.busy}
          onPress={handleDisable}
        />
      ) : (
        <Button
          title="Enable push notifications"
          disabled={!push.supported || push.permission === 'denied'}
          loading={push.busy}
          onPress={handleEnable}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  iconShell: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    height: 42,
    justifyContent: 'center',
    width: 42,
  },
  panelBody: {
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  statusCopy: {
    flex: 1,
    gap: spacing.xs,
  },
  statusRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: spacing.md,
  },
  statusText: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  statusTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
});
